import {
  LogOut,
  MessageSquareCode,
  MoreVertical,
  PenLine,
  Pin,
  Share2,
  Trash2,
  Users,
} from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../../auth/useAuth';
import { LiksMenu, type LiksMenuItem } from '../LiksMenu/LiksMenu';
import { LiksTooltip } from '../LiksTooltip/LiksTooltip';
import type { SessionItemProps } from './chatSessionItemTypes';
import './ChatSessionItem.css';

export function ChatSessionItem({
  session,
  onSessionClick,
  openMenuSessionId,
  onMenuOpenChange,
  onRenameRequest,
  onDeleteRequest,
  onShareRequest,
  onSystemPromptRequest,
  onPinToggle,
  translate,
}: SessionItemProps) {
  const { user } = useAuth();
  const isOwner = session.ownerId === user?.id;

  const menuItems: LiksMenuItem[] = [
    {
      id: 'rename',
      type: 'button',
      label: translate('chatSessions.rename'),
      icon: <PenLine strokeWidth={2} aria-hidden />,
      onClick: () => onRenameRequest({ id: session.id, title: session.title }),
    },
    {
      id: 'share',
      type: 'button',
      label: isOwner
        ? translate('chatSessions.share')
        : translate('chatSessions.participants'),
      icon: isOwner ? (
        <Share2 strokeWidth={2} aria-hidden />
      ) : (
        <Users strokeWidth={2} aria-hidden />
      ),
      onClick: () =>
        onShareRequest({
          id: session.id,
          title: session.title,
          isOwner,
        }),
    },
    {
      id: 'pin',
      type: 'button',
      label: session.isPinned
        ? translate('chatSessions.unpinChat')
        : translate('chatSessions.pinChat'),
      icon: <Pin strokeWidth={2} aria-hidden />,
      onClick: () => onPinToggle(session.id),
    },
    {
      id: 'systemPrompt',
      type: 'button',
      label: translate('chatSessions.systemPrompt'),
      icon: <MessageSquareCode strokeWidth={2} aria-hidden />,
      onClick: () => onSystemPromptRequest(session),
    },
    {
      id: 'delete',
      type: 'button',
      label: isOwner
        ? translate('chatSessions.deleteChat')
        : translate('chatSessions.leaveChat'),
      icon: isOwner ? (
        <Trash2 strokeWidth={2} aria-hidden />
      ) : (
        <LogOut strokeWidth={2} aria-hidden />
      ),
      className: 'liks-chat-session__menu-item--danger',
      onClick: () =>
        onDeleteRequest({
          id: session.id,
          title: session.title,
          isOwner,
        }),
    },
  ];

  return (
    <li
      className={[
        'liks-chat-session-item',
        openMenuSessionId === session.id && 'liks-chat-session-item--menu-open',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <NavLink
        to={`/platform-chat/${session.id}`}
        className={({ isActive }) =>
          [
            'liks-chat-session',
            isActive && 'liks-chat-session--active',
            session.hasNew && 'liks-chat-session--has-new',
          ]
            .filter(Boolean)
            .join(' ')
        }
        onClick={onSessionClick}
      >
        {session.hasNew && (
          <LiksTooltip
            content={translate('chatSessions.hasNewContent')}
            placement="right"
            describeChildren
            className="liks-chat-session__new-dot-wrap"
          >
            <span className="liks-chat-session__new-dot" aria-hidden />
          </LiksTooltip>
        )}
        <div className="liks-chat-session__content">
          <div className="liks-chat-session__title-row">
            {session.sharedAs === 'owner' && (
              <LiksTooltip
                content={translate('chatSessions.sharedByMe')}
                placement="right"
                describeChildren
                className="liks-chat-session__shared-icon-wrap"
              >
                <Share2
                  className="liks-chat-session__shared-icon liks-chat-session__shared-icon--owned"
                  strokeWidth={2}
                  aria-hidden
                />
              </LiksTooltip>
            )}
            {session.sharedAs === 'participant' && (
              <LiksTooltip
                content={translate('chatSessions.sharedWithMe')}
                placement="right"
                describeChildren
                className="liks-chat-session__shared-icon-wrap"
              >
                <Users
                  className="liks-chat-session__shared-icon liks-chat-session__shared-icon--joined"
                  strokeWidth={2}
                  aria-hidden
                />
              </LiksTooltip>
            )}
            <span className="liks-chat-session__title">{session.title}</span>
          </div>
          {session.lastMessage !== '' && (
            <span className="liks-chat-session__preview">
              {session.lastMessage}
            </span>
          )}
        </div>
      </NavLink>

      <LiksMenu
        className="liks-chat-session__menu"
        triggerClassName="liks-chat-session__menu-trigger"
        panelClassName="liks-chat-session__menu-panel"
        itemClassName="liks-chat-session__menu-item"
        trigger={
          <MoreVertical
            className="liks-chat-session__menu-icon"
            strokeWidth={2.5}
            aria-hidden
          />
        }
        items={menuItems}
        placement="down"
        portalPanel
        showChevron={false}
        isOpen={openMenuSessionId === session.id}
        onOpenChange={(isOpen) => onMenuOpenChange(isOpen ? session.id : null)}
        aria-label={translate('chatSessions.sessionActions', {
          title: session.title,
        })}
      />
    </li>
  );
}
